import {
  FluidAddressFields,
  FluidFieldAlias,
  FluidFormConfig,
  FluidFormElementState,
  FluidFormLayout,
  FluidFormState,
  FluidLocale,
  FluidTheme,
  FluidThemeInterface,
  FluidTranslationSet,
} from '@lmig/fluid-core';
import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';
import { timer } from 'rxjs';

import {
  applyElementOutputFormatting,
  getValue,
} from '../../utils/forms/form-method.utils';
import { Utils } from '../../utils/utils';
import { addressFormLocale } from './address-form.config.utils';
import {
  FluidAddressFormConfig,
  FluidAddressFormConfiguration,
} from './fluid-address-form-config.class';

/**
 * @displayName Address Form
 * @experimental This component is still in draft format.
 * @internal
 */
@Component({
  tag: 'fluid-address-form',
  shadow: true,
})
export class FluidAddressForm implements FluidThemeInterface {
  // ================================================================ //
  // -- Host Element

  /**
   * Get a reference to the host element
   */
  @Element() host: HTMLElement;

  // ================================================================ //
  // -- Props

  /**
   * Configures the address form as a standalone element.
   */
  @Prop({ mutable: true }) config: FluidAddressFormConfiguration;

  // ================================================================ //
  // -- Component State

  @State() _elementState: FluidFormElementState = {};

  // ================================================================ //
  // -- State

  /**
   * Holds the configuration for the address form.
   */
  @State() _config: FluidFormConfig<any>;

  /**
   * If true, the form is re-rendering so should show the loading spinner
   */
  @State() _loading = false;

  @State() _currentFormLocale = 'US';

  // ================================================================ //
  // -- State

  _addressFormConfig: FluidAddressFormConfig;
  _formValue: Record<string, any> = {};

  /**
   * Allows the country of the address form to be explicitly set from
   * above.
   * @param country
   */
  @Method() async setCountry(country: FluidLocale) {
    await this._innerFormRef.setElementValue('country', [country]);
    this._formValue.country = [country];
    this.setAddressFormConfig(
      addressFormLocale(country, this.fieldAlias, this.translations)
    );
    return Promise.resolve();
  }

  // ================================================================ //
  // -- Form Pattern Required Events

  @Method() async getValue() {
    return getValue(this);
  }

  @Method() async setValue(value: FluidFieldAlias<FluidAddressFields>) {
    return this._innerFormRef.setFormValue(value);
  }

  @Method() async reset(clear = false) {
    return this._innerFormRef.reset(clear);
  }

  @Method() async markTouchedAndValidate() {
    await this._innerFormRef.markTouchedAndValidate();
    return Promise.resolve(this._elementState);
  }

  @Method() async validate() {
    return Promise.resolve({
      name: this.dataPath,
      errors: this._elementState.errors,
      valid: (this._elementState.errors || []).length === 0,
    });
  }

  @Method() async setParentValue(formState: FluidFormState) {
    this._parentState = formState;
  }

  // ================================================================ //
  // -- Own Properties

  _parentState: FluidFormState;
  _innerFormRef: HTMLFluidFormElement;

  // ================================================================ //
  // -- Component Events

  /**
   * Emits the element state when its value changes
   */
  @Event() valueChange: EventEmitter<FluidFormElementState>;

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
    Utils.selectiveMapConfigToLocal(this, this.config);
    this.setAddressFormConfig(
      addressFormLocale(
        FluidLocale.UNITED_STATES,
        this.fieldAlias,
        this.translations
      )
    );
  }

  /**
   * Reconfigures the form whenever a new configuration is loaded
   */
  configureForm() {
    this._loading = true;
    this._config = undefined;
    timer(500).subscribe(() => {
      this._config = {
        formName: this.dataPath,
        layout: FluidFormLayout.ROW,
        rowOptions: this._addressFormConfig.layout,
        elements: this._addressFormConfig.elements.map((el) => ({
          ...el,
          disabled: this.disabled,
        })),
      };
      this._loading = false;
    });
  }

  /**
   * If the locale has changed, reconfigure the form with the new
   * locale-based configuration.
   * @param config
   */
  setAddressFormConfig(config: FluidAddressFormConfig) {
    if (this._addressFormConfig?.locale !== config.locale) {
      this._addressFormConfig = config;
      this.configureForm();
    }
  }

  /**
   * Handles the country field changing.
   * @param event
   */
  handleValueChange(event: CustomEvent<FluidFormElementState>) {
    const { detail } = event;
    this._formValue[detail.name] = detail.value;
    if (detail.name === 'country' && detail.value?.length > 0) {
      this.setAddressFormConfig(
        addressFormLocale(detail.value[0], this.fieldAlias, this.translations)
      );
    }
  }

  /**
   * Main Render function
   */
  render() {
    const componentWrapper = `fluid-address-form ${this.theme}`;
    return (
      <div class={componentWrapper}>
        <fluid-loading-spinner
          container={true}
          height={400}
          loading={this._loading}
          muted={true}>
          {this._config && (
            <fluid-form
              ref={(ref) => (this._innerFormRef = ref)}
              fromData={this._formValue}
              onFormChanged={(event: CustomEvent<FluidFormState>) => {
                const { rawValue, errors } = event.detail;
                this._elementState = {
                  name: this.dataPath,
                  valid: Object.keys(errors).length === 0,
                  touched: true,
                  errors: Object.keys(errors).map((error) => error),
                  value: rawValue,
                };
                this.valueChange.emit(applyElementOutputFormatting(this));
              }}
              onValueChanged={(event: CustomEvent<FluidFormElementState>) =>
                this.handleValueChange(event)
              }
              config={this._config}
            />
          )}
        </fluid-loading-spinner>
      </div>
    );
  }

  // ================================================================ //
  // -- Shadowed Props

  /**
   * What theme settings should the component use?
   * @type {string}
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * Allows you to pass a list of translations for the translatable elements
   * of the address fields.
   * @experimental
   * @internal
   */
  @Prop({ mutable: true })
  translations: FluidTranslationSet<FluidAddressFields>;

  /**
   * Allows you to override the data paths at which the values of the fields
   * are stored.
   */
  @Prop({ mutable: true }) fieldAlias: FluidFieldAlias<FluidAddressFields>;

  /**
   * If true, entire address form will be disabled.
   */
  @Prop({ mutable: true }) disabled: boolean;

  /**
   * The path to the element value in the parent form.
   */
  @Prop({ mutable: true }) dataPath: string;

  /**
   * @internal
   */
  @Prop({ mutable: true }) parentState: FluidFormState;

  /**
   * @internal
   */
  @Prop({ mutable: true }) autoValidateChildren: boolean;

  @Watch('parentState') async onParentStateChange(newState: FluidFormState) {
    this._parentState = newState;
    if (this.autoValidateChildren) {
      await this.validate();
    }
  }
}
